import React, { useEffect, useState, useRef } from 'react';
import { api } from '../services/api';
import UbigeoInput from './UbigeoInput';

const STORAGE_KEY = 'epg_form';

const INITIAL = {
  docType: '', docNumber: '', name: '', paternal: '', maternal: '',
  gender: '', birthDate: '', cell: '', email: '', address: '',
  ubigeoBirthId: null, ubigeoBirthText: '',
  ubigeoResidentId: null, ubigeoResidentText: '',
};

function load() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null') || {};
  } catch { return {}; }
}

export default function Step2PersonalData({ onNext, onBack, addToast }) {
  const [docTypes, setDocTypes] = useState([]);
  const [form, setForm] = useState({ ...INITIAL, ...load() });
  const [errors, setErrors] = useState({});
  const [dniLoading, setDniLoading] = useState(false);
  const dniDebounce = useRef(null);

  useEffect(() => {
    api.getDocumentTypes()
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        setDocTypes(list);
        setForm((prev) => {
          if (prev.docType) return prev;
          const dni = list.find((d) =>
            (d.abbreviation || d.denomination || '').toUpperCase().includes('DNI')
          );
          if (!dni) return prev;
          const next = { ...prev, docType: dni.id };
          try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch {}
          return next;
        });
      })
      .catch(() => {});
  }, []);

  const set = (field, value) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch {}
      return next;
    });
    if (errors[field]) setErrors((e) => { const n = { ...e }; delete n[field]; return n; });
  };

  const isDniType = (docTypeId) => {
    const found = docTypes.find((d) => String(d.id) === String(docTypeId));
    const abbr = (found?.abbreviation || found?.denomination || '').toUpperCase();
    return !abbr || abbr === 'DNI';
  };

  const handleDniChange = (value) => {
    const digits = value.replace(/\D/g, '');
    set('docNumber', digits);
    if (digits.length === 8 && isDniType(form.docType)) {
      clearTimeout(dniDebounce.current);
      dniDebounce.current = setTimeout(() => searchDni(digits, true), 500);
    }
  };

  const searchDni = async (docNumber, silent = false) => {
    const num = docNumber || form.docNumber;
    if (!num || num.length !== 8) {
      if (!silent) addToast('warning', 'Ingrese un DNI válido de 8 dígitos.');
      return;
    }
    setDniLoading(true);
    try {
      const data = await api.searchDni(num);
      const gender = (data.sexo || '').toUpperCase();
      const bdate  = data.fechaNacimiento || data.fecha_nacimiento || '';
      setForm((prev) => {
        const next = {
          ...prev,
          name:      data.nombres         || prev.name      || '',
          paternal:  data.apellidoPaterno  || data.apellido_paterno || prev.paternal  || '',
          maternal:  data.apellidoMaterno  || data.apellido_materno || prev.maternal  || '',
          address:   data.direccion        || prev.address   || '',
          cell:      data.celular          || prev.cell      || '',
          email:     data.email            || prev.email     || '',
          birthDate: bdate                 || prev.birthDate || '',
          gender:    gender === 'M' || gender === 'MASCULINO' ? 'M'
                   : gender === 'F' || gender === 'FEMENINO'  ? 'F'
                   : prev.gender,
          ubigeoResidentId:   data.ubigeo?.id    || prev.ubigeoResidentId,
          ubigeoResidentText: data.ubigeo?.label || prev.ubigeoResidentText,
        };
        try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch {}
        return next;
      });
      setErrors((prev) => {
        const next = { ...prev };
        ['name', 'paternal', 'maternal', 'address', 'cell', 'email', 'birthDate', 'gender', 'docNumber'].forEach((f) => {
          delete next[f];
        });
        if (data.ubigeo?.id) delete next.ubigeoResident;
        return next;
      });
      const hasContact = data.celular || data.email;
      addToast('success', hasContact
        ? 'Datos y contacto completados automáticamente.'
        : 'Datos completados. Ingrese su celular y correo.');
    } catch {
      if (!silent) addToast('warning', 'No se encontró el DNI en RENIEC. Complete los datos manualmente.');
    } finally {
      setDniLoading(false);
    }
  };

  const str = (v) => String(v || '');

  const validate = () => {
    const e = {};
    if (!form.docType) e.docType = true;
    if (!str(form.docNumber).trim()) e.docNumber = true;
    if (!str(form.name).trim()) e.name = true;
    if (!str(form.paternal).trim()) e.paternal = true;
    if (!str(form.maternal).trim()) e.maternal = true;
    if (!form.gender) e.gender = true;
    if (!form.birthDate) e.birthDate = true;
    if (!str(form.cell).trim()) e.cell = true;
    if (!str(form.email).trim()) e.email = true;
    if (!str(form.address).trim()) e.address = true;
    if (!form.ubigeoBirthId) e.ubigeoBirth = true;
    if (!form.ubigeoResidentId) e.ubigeoResident = true;
    setErrors(e);
    if (Object.keys(e).length) {
      addToast('warning', 'Complete todos los campos obligatorios.');
      return false;
    }
    return true;
  };

  const handleNext = () => { if (validate()) onNext(form); };

  const inp = (field, extra = {}) => ({
    className: `form-input${errors[field] ? ' input-error' : ''}`,
    value: str(form[field]),
    onChange: (e) => set(field, e.target.value),
    ...extra,
  });

  const sel = (field, extra = {}) => ({
    className: `form-select${errors[field] ? ' input-error' : ''}`,
    value: form[field],
    onChange: (e) => set(field, e.target.value),
    ...extra,
  });

  return (
    <div className="card">
      <div className="card-header">
        <div className="card-icon">👤</div>
        <div>
          <div className="card-title">Datos Personales</div>
          <div className="card-sub">Ingrese su información personal. Con DNI puede usar la búsqueda automática.</div>
        </div>
      </div>

      <div className="section-title">Documento de Identidad</div>
      <div className="form-grid">
        <div className="form-group">
          <label className="form-label">Tipo de Documento</label>
          <select {...sel('docType')}>
            <option value="">Seleccione...</option>
            {docTypes.map((d) => (
              <option key={d.id} value={d.id}>
                {d.denomination}{d.abbreviation ? ` (${d.abbreviation})` : ''}
              </option>
            ))}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Número de Documento</label>
          <div className="input-with-btn">
            <input
              type="text"
              {...inp('docNumber', {
                maxLength: 12,
                onChange: (e) => handleDniChange(e.target.value),
              })}
              placeholder="Ej: 12345678"
            />
            <button
              type="button"
              className="btn-search"
              onClick={() => searchDni(form.docNumber, false)}
              disabled={dniLoading || !form.docType || !form.docNumber}
            >
              {dniLoading ? '...' : '🔍'}
            </button>
          </div>
        </div>
      </div>

      <div className="section-title">Nombres y Apellidos</div>
      <div className="form-grid form-grid-3">
        <div className="form-group">
          <label className="form-label">Nombres</label>
          <input type="text" {...inp('name')} placeholder="Nombres completos" />
        </div>
        <div className="form-group">
          <label className="form-label">Apellido Paterno</label>
          <input type="text" {...inp('paternal')} placeholder="Apellido paterno" />
        </div>
        <div className="form-group">
          <label className="form-label">Apellido Materno</label>
          <input type="text" {...inp('maternal')} placeholder="Apellido materno" />
        </div>
      </div>

      <div className="section-title">Información Complementaria</div>
      <div className="form-grid">
        <div className="form-group">
          <label className="form-label">Género</label>
          <select {...sel('gender')}>
            <option value="">— Seleccione —</option>
            <option value="M">Masculino</option>
            <option value="F">Femenino</option>
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Fecha de Nacimiento</label>
          <input type="date" {...inp('birthDate')} />
        </div>
        <div className="form-group">
          <label className="form-label">Celular</label>
          <input type="tel" {...inp('cell')} placeholder="Ej: 987654321" maxLength={15} />
        </div>
        <div className="form-group">
          <label className="form-label">Correo Electrónico</label>
          <input type="email" {...inp('email')} placeholder="correo@ejemplo.com" />
        </div>
      </div>

      <div className="form-group" style={{ marginTop: 0 }}>
        <label className="form-label">Dirección</label>
        <input type="text" {...inp('address')} placeholder="Dirección completa" />
      </div>

      <div className="section-title">Ubicación</div>
      <div className="form-grid">
        <UbigeoInput
          id="ubigeo-birth"
          label="Lugar de Nacimiento"
          value={form.ubigeoBirthText}
          ubigeoId={form.ubigeoBirthId}
          hasError={!!errors.ubigeoBirth}
          onChange={(id, text) => {
            setForm((prev) => {
              const next = { ...prev, ubigeoBirthId: id, ubigeoBirthText: text };
              try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch {}
              return next;
            });
          }}
        />
        <UbigeoInput
          id="ubigeo-resident"
          label="Lugar de Residencia"
          value={form.ubigeoResidentText}
          ubigeoId={form.ubigeoResidentId}
          hasError={!!errors.ubigeoResident}
          onChange={(id, text) => {
            setForm((prev) => {
              const next = { ...prev, ubigeoResidentId: id, ubigeoResidentText: text };
              try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch {}
              return next;
            });
          }}
        />
      </div>

      <div className="step-actions">
        <button className="btn-ghost" onClick={onBack}>← Atrás</button>
        <button className="btn-primary" onClick={handleNext}>Continuar →</button>
      </div>
    </div>
  );
}
